import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { GoogleMap, useLoadScript, Autocomplete } from '@react-google-maps/api';
import './styles/Landlord.css';
import ApartmentHouseForm from './PropertyForms/ApartmentHouseForm';
import LodgeForm from './PropertyForms/LodgeForm';
import RoomForm from './PropertyForms/RoomForm';
import BoardingForm from './PropertyForms/BoardingForm';

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [autoCompleteError, setAutoCompleteError] = useState('');
  const [videoPreview, setVideoPreview] = useState(null);
  const [deletedImages, setDeletedImages] = useState([]);
  const base_url = 'https://api.homehive.co.zw/api';

  const [formData, setFormData] = useState({
    property_type: '',
    description: '',
    monthly_rental: '',
    location: '',
    latitude: '',
    longitude: '',
    availability_status: '',
    property_tour: null,
    has_power_backup: false,
    power_backup: '',
    water_source: '',
    is_deposit_required: false,
    deposit_amount: '',
    is_gated: false,
    nearby_hospital: '',
    nearby_school: '',
    type_of_bathroom: '',
    has_parking_space: false,
    parking_slots: '',
    bedrooms: '',
    is_furnished: false,
    has_pool: false,
    has_gym: false,
    has_cctv: false,
    has_security: false,
    pets_allowed: false,
    has_electricity: false,
    // Lodge specific fields
    price_per_night: '',
    total_rooms: '',
    has_restaurant: false,
    has_bar: false,
    has_outdoor_space: false,
    has_wifi: false,
    has_tv: false,
    has_air_conditioning: false,
    max_occupants: '',
    has_study_area: false,
    has_common_area: false,
    has_kitchen: false,
    gender_restriction: '',
  });

  const propertyTypes = [
    { value: 'apartment', label: 'Apartment', icon: 'building' },
    { value: 'full_house', label: 'House', icon: 'home' },
    { value: 'room', label: 'Room', icon: 'door-open' },
    { value: 'lodge', label: 'Lodge', icon: 'door-open' },
    { value: 'boarding_house', label: 'Boarding House', icon: 'warehouse' }
  ];

  const [autocomplete, setAutocomplete] = useState(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY',
    libraries: ['places']
  });
  const autocompleteRef = useRef(null);

  // Fetch property data when component mounts
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get(`${base_url}/properties/${id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const property = response.data;
        setPropertyData(property);
        
        // Set form data from property
        const initialFormData = {
          property_type: property.property_type || '',
          description: property.description || '',
          monthly_rental: property.monthly_rental || property.price || '',
          location: property.location || '',
          latitude: property.latitude || '',
          longitude: property.longitude || '',
          availability_status: property.availability_status || '',
          has_power_backup: property.has_power_backup || false,
          power_backup: property.power_backup || '',
          water_source: property.water_source || '',
          is_deposit_required: property.is_deposit_required || false,
          deposit_amount: property.deposit_amount || '',
          is_gated: property.is_gated || false,
          nearby_hospital: property.nearby_hospital || '',
          nearby_school: property.nearby_school || '',
          type_of_bathroom: property.type_of_bathroom || '',
          has_parking_space: property.has_parking_space || false,
          parking_slots: property.parking_slots || '',
          bedrooms: property.bedrooms || '',
          is_furnished: property.is_furnished || false,
          has_pool: property.has_pool || false,
          has_gym: property.has_gym || false,
          has_cctv: property.has_cctv || false,
          has_security: property.has_security || false,
          pets_allowed: property.pets_allowed || false,
          has_electricity: property.has_electricity || false,
          // Lodge specific fields
          price_per_night: property.price_per_night || '',
          total_rooms: property.total_rooms || '',
          has_restaurant: property.has_restaurant || false,
          has_bar: property.has_bar || false,
          has_outdoor_space: property.has_outdoor_space || false,
          has_wifi: property.has_wifi || false,
          has_tv: property.has_tv || false,
          has_air_conditioning: property.has_air_conditioning || false,
          max_occupants: property.max_occupants || '',
          has_study_area: property.has_study_area || false,
          has_common_area: property.has_common_area || false,
          has_kitchen: property.has_kitchen || false,
          gender_restriction: property.gender_restriction || '',
        };
        
        setFormData(initialFormData);
        
        // Set preview images
        if (property.images && property.images.length > 0) {
          const previews = property.images.map(img => `${base_url}${img.image}`);
          setPreviewImages(previews);
        }
        
        // Set video preview if exists
        if (property.property_tour) {
          setVideoPreview(`${base_url}${property.property_tour}`);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching property:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to load property details',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        navigate('/landlord/properties');
      }
    };

    fetchProperty();
  }, [id, navigate]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    console.log('Validating form data:', formData);
    
    if (!formData.property_type) {
      errors.property_type = 'Property type is required';
    }
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    if (!formData.location) {
      errors.location = 'Location is required';
    }

    // Property type specific validation
    switch (formData.property_type) {
      case 'apartment':
      case 'full_house':
        if (!formData.monthly_rental) {
          errors.monthly_rental = 'Monthly rental is required';
        }
        if (!formData.bedrooms) {
          errors.bedrooms = `Bedrooms are required for ${formData.property_type}s`;
        }
        if (!formData.water_source) {
          errors.water_source = 'Water source is required';
        }
        if (formData.has_parking_space && !formData.parking_slots) {
          errors.parking_slots = 'Number of parking slots is required when parking space is available';
        }
        if (formData.has_power_backup && !formData.power_backup) {
          errors.power_backup = 'Power backup details are required when power backup is available';
        }
        if (formData.is_deposit_required && !formData.deposit_amount) {
          errors.deposit_amount = 'Deposit amount is required when deposit is required';
        }
        break;

      case 'room':
        if (!formData.monthly_rental) {
          errors.monthly_rental = 'Monthly rental is required for rooms';
        }
        if (!formData.water_source) {
          errors.water_source = 'Water source is required';
        }
        if (!formData.type_of_bathroom) {
          errors.type_of_bathroom = 'Bathroom type is required for rooms';
        }
        if (formData.has_power_backup && !formData.power_backup) {
          errors.power_backup = 'Power backup details are required when power backup is available';
        }
        if (formData.is_deposit_required && !formData.deposit_amount) {
          errors.deposit_amount = 'Deposit amount is required when deposit is required';
        }
        break;

      case 'lodge':
        if (!formData.price_per_night) {
          errors.price_per_night = 'Price per night is required';
        }
        if (!formData.type_of_bathroom) {
          errors.type_of_bathroom = 'Bathroom type is required for lodges';
        }
        break;

      case 'boarding_house':
        if (!formData.max_occupants) {
          errors.max_occupants = 'Maximum occupants is required';
        }
        if (!formData.gender_restriction) {
          errors.gender_restriction = 'Gender restriction is required';
        }
        break;

      default:
        break;
    }

    return errors;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submit triggered');
    console.log('Form data:', formData);
    
    // Validate form
    const formErrors = validateForm();
    console.log('Form errors:', formErrors);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      console.log('Validation failed, stopping submission');
      return;
    }
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      console.log('Images to submit:', images.length);
      console.log('Deleted images:', deletedImages);
      console.log('Preview images count:', previewImages.length);
      
      // Add form fields to FormData
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add new images to FormData
      images.forEach((image, index) => {
        console.log(`Adding image ${index + 1}:`, image.name, image.size);
        formDataToSend.append('images', image);
      });
      
      // Add property tour if it's a new file
      if (formData.property_tour && typeof formData.property_tour !== 'string') {
        console.log('Adding property tour:', formData.property_tour.name);
        formDataToSend.append('property_tour', formData.property_tour);
      }
      
      // Add deleted images if any
      if (deletedImages.length > 0) {
        console.log('Marking images for deletion:', deletedImages);
        formDataToSend.append('deleted_images', JSON.stringify(deletedImages));
      }
      
      // Log FormData contents
      console.log('FormData entries:');
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0], pair[1]);
      }
      
      const response = await axios.put(
        `${base_url}/properties/${id}/`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      Swal.fire({
        title: 'Success!',
        text: 'Property has been updated successfully',
        icon: 'success',
        confirmButtonColor: '#1a237e'
      }).then(() => {
        navigate('/landlord/properties');
      });
    } catch (error) {
      console.error('Error updating property:', error);
      const errorMessage = error.response?.data?.detail || 
                        Object.values(error.response?.data || {})[0] || 
                        'Failed to update property';
      
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Calculate current images count (existing + new)
    const existingImagesCount = (propertyData?.images?.length || 0) - deletedImages.length;
    const newImagesCount = images.length;
    const totalCurrentImages = existingImagesCount + newImagesCount;
    
    if (files.length + totalCurrentImages > 5) {
      Swal.fire({
        title: 'Error',
        text: 'Maximum 5 images allowed',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: `File ${file.name} is too large. Maximum size is 10MB`,
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    console.log('Adding valid files:', validFiles.map(f => ({ name: f.name, size: f.size })));

    // Store the actual files
    setImages(prevImages => {
      const newImages = [...prevImages, ...validFiles];
      console.log('Updated images array:', newImages.length, 'files');
      return newImages;
    });

    // Create preview URLs for new files
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setPreviewImages(prevPreviews => {
      const updated = [...prevPreviews, ...newPreviews];
      console.log('Updated preview images:', updated.length, 'previews');
      return updated;
    });
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    const existingImagesCount = propertyData?.images?.length || 0;
    const isExistingImage = index < existingImagesCount;
    
    if (isExistingImage) {
      // Mark existing image for deletion
      const imageToDelete = propertyData.images[index];
      if (imageToDelete && imageToDelete.id) {
        setDeletedImages(prev => [...prev, imageToDelete.id]);
      }
    } else {
      // Remove new image from the images array
      const newImageIndex = index - existingImagesCount;
      setImages(prev => prev.filter((_, i) => i !== newImageIndex));
      
      // Revoke the blob URL to prevent memory leaks
      const previewUrl = previewImages[index];
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    }
    
    // Remove from preview images array
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  // Handle video upload
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        Swal.fire({
          title: 'Error',
          text: 'Please upload a valid video file',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return;
      }

      // Check file size (limit to 100MB)
      if (file.size > 100 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: 'Video file size should not exceed 100MB',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
        return;
      }

      setFormData(prev => ({
        ...prev,
        property_tour: file
      }));

      // Create video preview URL
      if (videoPreview && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
      const videoURL = URL.createObjectURL(file);
      setVideoPreview(videoURL);
    }
  };

  // Handle video removal
  const handleRemoveVideo = () => {
    if (videoPreview && videoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview(null);
    setFormData(prev => ({
      ...prev,
      property_tour: null
    }));
  };

  // Setup Google Maps autocomplete
  const onLoad = (autoC) => {
    setAutocomplete(autoC);
    autocompleteRef.current = autoC;
  };

  const onPlaceChanged = () => {
    try {
      const autoC = autocomplete || autocompleteRef.current;
      if (!autoC) {
        setAutoCompleteError('Autocomplete not initialized.');
        return;
      }
      const place = autoC.getPlace();
      if (!place || !place.geometry) {
        setAutoCompleteError('No place details available.');
        return;
      }
      const address = place.formatted_address || '';
      const lat = place.geometry.location?.lat();
      const lng = place.geometry.location?.lng();
      if (!lat || !lng) {
        setAutoCompleteError('No coordinates found for this place.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        location: address,
        latitude: lat,
        longitude: lng
      }));
      setAutoCompleteError('');
    } catch (err) {
      setAutoCompleteError('Error getting place details.');
      console.error('Autocomplete error:', err);
    }
  };

  // Render property type specific forms
  const renderPropertyTypeFields = () => {
    switch (formData.property_type) {
      case 'apartment':
      case 'full_house':
        return <ApartmentHouseForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'room':
        return <RoomForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'lodge':
        return <LodgeForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      case 'boarding_house':
        return <BoardingForm formData={formData} handleInputChange={handleInputChange} errors={errors} />;
      default:
        return null;
    }
  };

  // Use current location
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        title: 'Error',
        text: 'Geolocation is not supported by your browser.',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      return;
    }
    
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
        if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
          throw new Error('Google Maps API key is missing or invalid.');
        }
        
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
        const data = await response.json();
        const address = data.results && data.results[0] ? data.results[0].formatted_address : '';
        
        setFormData(prev => ({
          ...prev,
          location: address,
          latitude: lat,
          longitude: lng
        }));
        
        setAutoCompleteError('');
      } catch (error) {
        setAutoCompleteError('Failed to retrieve address from coordinates.');
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to retrieve address from coordinates.',
          icon: 'error',
          confirmButtonColor: '#1a237e'
        });
      }
      
    setIsLoading(false);
    }, (error) => {
      setAutoCompleteError('Unable to retrieve your location.');
      Swal.fire({
        title: 'Error',
        text: 'Unable to retrieve your location.',
        icon: 'error',
        confirmButtonColor: '#1a237e'
      });
      
      setIsLoading(false);
    });
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      // Cleanup preview URLs to prevent memory leaks
      previewImages.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      if (videoPreview && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [previewImages, videoPreview]);

  // Render loading state
  if (isLoading) {
    return (
        <div className="loader-container">
          <div className="beat-loader">
            <div className="beat"></div>
            <div className="beat"></div>
            <div className="beat"></div>
          </div>
          <p className="loading-text">Loading Property...</p>
        </div>
    );
  }

  return (
    <div className="add-property-wrapper">
      <div className="add-property-container">
        <div className="property-form-card">
          <div className="add-property-header">

            <div className="header-content">
              <h1>
                <i className="fas fa-edit fa-bounce"></i>
                Edit Property
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="add-property-form">
            {/* Property Type Display */}
            <div className="form-section">
              <div className="section-header">
                <h2><i className="fas fa-home"></i> Property Type</h2>
              </div>
              <div className="property-type-grid">
                {propertyTypes.map(type => {
                  const isSelected = formData.property_type === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                    disabled={true} // Property type cannot be changed after creation
                      className={`property-type-card${isSelected ? ' selected' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '14px 16px',
                        borderRadius: '8px',
                        border: isSelected ? '2px solid #1a237e' : '1px solid #e0e0e0',
                        background: isSelected ? '#e8eaf6' : '#ffffff',
                        color: isSelected ? '#1a237e' : '#999',
                        cursor: 'not-allowed',
                        boxShadow: isSelected ? '0 2px 8px rgba(26,35,126,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
                        opacity: isSelected ? 1 : 0.6
                      }}
                      aria-pressed={isSelected}
                    >
                      <span style={{ fontWeight: 600 }}>{type.label}</span>
                    </button>
                  );
                })}
                </div>
              {errors.property_type && <div className="error-text" style={{ marginTop: '10px' }}>{errors.property_type}</div>}
            </div>

            {/* Basic Information */}
            <div className="form-section">
              <div className="section-header">
                <h2><i className="fas fa-info-circle"></i> Property Details</h2>
              </div>
              <div className="form-grid">
                {/* Description */}
                <div className="form-group full-width">
                  <label>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder="Write a compelling description of your property..."
                    rows="3"
                    className="fancy-input description-input"
                    inputMode="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  />
                  {errors.description && <div className="error-text">{errors.description}</div>}
                </div>

                {/* Price */}
                <div className="form-group">
                  <label>
                    {formData.property_type === 'lodge' ? 'Price Per Night (USD)' : 'Monthly Rent (USD)'}
                  </label>
                  <div className="price-input">
                    <input
                      type="number"
                      name={formData.property_type === 'lodge' ? 'price_per_night' : 'monthly_rental'}
                      value={formData.property_type === 'lodge' ? formData.price_per_night : formData.monthly_rental}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder={formData.property_type === 'lodge' ? 'Enter price per night' : 'Enter monthly rent'}
                      className="fancy-input"
                    />
                  </div>
                  {errors.price_per_night && <div className="error-text">{errors.price_per_night}</div>}
                  {errors.monthly_rental && <div className="error-text">{errors.monthly_rental}</div>}
                </div>

                <div className="form-group">
                  <label>
                    Location
                  </label>

                  {isLoaded ? (
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={onPlaceChanged}
                      ref={autocompleteRef}
                    >
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        placeholder="Search for address or place"
                        className="fancy-input"
                        style={{ flex: 1 }}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter street address"
                      className="fancy-input"
                      style={{ flex: 1 }}
                    />
                  )}
                  <button
                    type="button"
                    className="current-location-btn"
                    onClick={handleUseCurrentLocation}
                    disabled={isLoading}
                    title="Use my current location"
                    style={{ whiteSpace: 'nowrap', padding: '8px 12px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-location-arrow"></i>}
                    &nbsp;My Location
                  </button>
                </div>
                {autoCompleteError && (
                  <div style={{ color: 'red', marginTop: '4px' }}>{autoCompleteError}</div>
                )}
                {errors.location && <div className="error-text">{errors.location}</div>}

                {/* Property Type Specific Fields */}
                {renderPropertyTypeFields()}
              </div>
            </div>

            {/* Media Upload Section */}
            <div className="form-section">
              <div className="section-header">
                <h2><i className="fas fa-camera"></i> Property Media</h2>
              </div>
              <div className="media-upload-grid">
                {/* Image Upload */}
                <div className="media-upload-item">
                  <div className="upload-header">
                    <h3><i className="fas fa-images"></i> Property Images</h3>
                  </div>
                  <div className="image-upload-section">
                    <label 
                      htmlFor="property-images" 
                      className="upload-container"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                        const files = Array.from(e.dataTransfer.files);
                        handleImageChange({ target: { files } });
                      }}
                    >
                      <input
                        type="file"
                        id="property-images"
                        name="image"
                        onChange={handleImageChange}
                        multiple
                        accept="image/*"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="upload-content">
                        <div className="upload-icon">
                          <i className="fas fa-images"></i>
                        </div>
                        <h4>Upload Images</h4>
                        <p>Drag & drop or click</p>
                      </div>
                    </label>

                    {previewImages.length > 0 && (
                      <div className="preview-grid">
                        {previewImages.map((preview, index) => (
                          <div key={index} className="preview-card">
                            <img src={preview} alt={`Preview ${index + 1}`} />
                            <button 
                              type="button" 
                              className="remove-btn"
                              onClick={() => handleRemoveImage(index)}
                              title="Remove image"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                            <div className="image-number">Image {index + 1}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Upload */}
                <div className="media-upload-item">
                  <div className="upload-header">
                    <h3><i className="fas fa-video"></i> Property Video Tour</h3>
                  </div>
                  <div className="video-upload-section">
                    <label 
                      htmlFor="property-video" 
                      className="upload-container"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                        const files = Array.from(e.dataTransfer.files);
                        if (files.length > 0) {
                          handleVideoChange({ target: { files: [files[0]] } });
                        }
                      }}
                    >
                      <input
                        type="file"
                        id="property-video"
                        name="property_tour"
                        onChange={handleVideoChange}
                        accept="video/*"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="upload-content">
                        <div className="upload-icon">
                          <i className="fas fa-video"></i>
                        </div>
                        <h4>Upload Video Tour</h4>
                        <p>Drag & drop or click</p>
                      </div>
                    </label>

                    {videoPreview && (
                      <div className="video-preview">
                        <video controls className="preview-video">
                          <source src={videoPreview} type={typeof formData.property_tour === 'object' ? formData.property_tour.type : 'video/mp4'} />
                          Your browser does not support the video tag.
                        </video>
                        <button 
                          type="button" 
                          className="remove-btn"
                          onClick={handleRemoveVideo}
                          title="Remove video"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/landlord/properties')}
                className="cancel-btn"
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSaving}
               
              >
                {isSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
        </form>
        </div>
      </div>
    </div>
  );
};

// Update the style element with video preview styles to match AddProperty
const style = document.createElement('style');
style.innerHTML = `
.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 9999;
}

.beat-loader {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.beat {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #1a237e;
  animation: beat-animation 1.4s ease-in-out infinite both;
}

.beat:nth-child(1) {
  animation-delay: -0.32s;
}

.beat:nth-child(2) {
  animation-delay: -0.16s;
}

.beat:nth-child(3) {
  animation-delay: 0s;
}

@keyframes beat-animation {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.loading-text {
  color: #1a237e;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.media-upload-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-top: 20px;
}

.media-upload-item {
  flex: 1;
  min-width: 0;
}

.upload-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

.upload-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a237e;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-header h3 i {
  font-size: 16px;
}

.upload-content h4 {
  margin: 8px 0 4px 0;
  font-size: 16px;
  font-weight: 600;
}

.upload-content p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.video-upload-section {
  margin: 0;
}

.image-upload-section {
  margin: 0;
}

/* Responsive design */
@media screen and (max-width: 768px) {
  .media-upload-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.video-preview {
  margin-top: 20px;
  position: relative;
  max-width: 600px; /* Reduced from 100% to 600px */
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-video {
  width: 100%;
  max-height: 300px; /* Reduced from 400px to 300px */
  object-fit: contain;
  background: #000;
}

.video-preview .remove-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  z-index: 1;
}

.video-preview .remove-btn:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* Add responsive styles */
@media screen and (max-width: 768px) {
  .video-preview {
    max-width: 100%;
  }
  
  .preview-video {
    max-height: 250px;
  }
}

@media screen and (max-width: 480px) {
  .preview-video {
    max-height: 200px;
  }
}
`;
document.head.appendChild(style);

export default EditProperty; 